import { FirebaseError } from "firebase/app"
import { collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore"
import { deleteObject, getDownloadURL, ref, uploadString } from "firebase/storage"
import { db, storage } from "@/config/firebase"
import type {
  PersistedMonthlyReport,
  MeeshoCombinedReport
} from "@/features/profit-calculators/calculator-shared/types"
import {
  type ReportSourceFile,
  uploadReportFilesArchive
} from "@/features/profit-calculators/calculator-shared/services/reportFileArchive"
import { getReportMonthGenerationError } from "@/features/profit-calculators/calculator-shared/utils/reportMonthUtils"

const reportsCollection = (uid: string) => collection(db, "users", uid, "monthlyReports")

const buildReportId = (platform: "meesho" | "amazon", monthKey: string) => `${platform}_${monthKey}`

const detailStoragePath = (uid: string, reportId: string) => `${uid}/monthlyReports/${reportId}.json`

const listMonthlyReports = async (
  uid: string,
  platform: "meesho" | "amazon",
  year: number
): Promise<PersistedMonthlyReport[]> => {
  // Keep this query on one field so Firestore can use its automatic single-field index.
  // A user can have at most two reports per month, so filtering the bounded yearly result is inexpensive.
  const q = query(reportsCollection(uid), where("monthKey", ">=", `${year}-01`), where("monthKey", "<=", `${year}-12`))
  const snapshot = await getDocs(q)
  return snapshot.docs
    .map(d => d.data() as PersistedMonthlyReport)
    .filter(report => report.platform === platform)
    .sort((a, b) => b.monthKey.localeCompare(a.monthKey))
}

const getMonthlyReport = async (
  uid: string,
  platform: "meesho" | "amazon",
  monthKey: string
): Promise<PersistedMonthlyReport | null> => {
  const reportId = buildReportId(platform, monthKey)
  const snapshot = await getDoc(doc(reportsCollection(uid), reportId))
  return snapshot.exists() ? (snapshot.data() as PersistedMonthlyReport) : null
}

/** Fetches the full order/file detail JSON for a report, lazily, only when needed (e.g. order breakdown modal). */
const getMonthlyReportDetail = async (
  detailStorageRef: string
): Promise<Pick<MeeshoCombinedReport, "orders" | "files"> & { amazonMetadata?: unknown }> => {
  const url = await getDownloadURL(ref(storage, detailStorageRef))
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error("Failed to load report detail.")
  }
  return response.json()
}

const saveMonthlyReport = async (
  uid: string,
  platform: "meesho" | "amazon",
  monthKey: string,
  report: MeeshoCombinedReport,
  sourceFiles: ReportSourceFile[]
): Promise<PersistedMonthlyReport> => {
  const monthError = getReportMonthGenerationError(monthKey)
  if (monthError) throw new Error(monthError)

  const reportId = buildReportId(platform, monthKey)
  const storagePath = detailStoragePath(uid, reportId)
  const existing = await getMonthlyReport(uid, platform, monthKey)
  if (existing) {
    throw new Error("This report has already been generated and cannot be updated or deleted.")
  }

  let sourceFilesStoragePath: string | undefined
  try {
    const sourceArchive = await uploadReportFilesArchive(uid, platform, monthKey, sourceFiles)
    sourceFilesStoragePath = sourceArchive.storagePath

    const detailJson = JSON.stringify({
      orders: report.orders,
      files: report.files,
      amazonMetadata: (report as any).amazonMetadata
    })
    await uploadString(ref(storage, storagePath), detailJson, "raw", {
      contentType: "application/json",
      customMetadata: {
        reportId,
        monthKey,
        platform,
        reportYear: sourceArchive.reportYear,
        monthFolder: sourceArchive.monthFolder
      }
    })

    const totalOrders = report.totalOrders || 0
    const returnedCount = report.deliveredSplit?.returned?.count ?? 0
    const now = Date.now()
    const persisted: PersistedMonthlyReport = {
      platform,
      monthKey,
      periodLabel: report.periodLabel,
      reportId,
      totalOrders,
      totalUnits: report.totalUnits,
      totalSettlement: report.totalSettlement,
      totalPurchaseCost: report.totalPurchaseCost,
      totalAdsCost: report.totalAdsCost,
      totalReferral: report.totalReferral,
      netProfit: report.netProfit,
      roiPct: report.totalPurchaseCost ? (report.netProfit / report.totalPurchaseCost) * 100 : 0,
      returnPct: totalOrders ? (returnedCount / totalOrders) * 100 : 0,
      deliveredSplit: report.deliveredSplit,
      undeliveredSplit: report.undeliveredSplit,
      skuReports: report.skuReports,
      detailStorageRef: storagePath,
      sourceFilesStorageRef: sourceArchive.storagePath,
      reportYear: sourceArchive.reportYear,
      monthFolder: sourceArchive.monthFolder,
      createdAt: now,
      updatedAt: now
    }

    await setDoc(doc(reportsCollection(uid), reportId), persisted, {
      merge: false
    })
    return persisted
  } catch (error) {
    await Promise.allSettled([
      deleteStorageObjectIfPresent(storagePath),
      deleteStorageObjectIfPresent(sourceFilesStoragePath)
    ])
    throw error
  }
}

async function deleteStorageObjectIfPresent(storagePath?: string): Promise<void> {
  if (!storagePath) return
  try {
    await deleteObject(ref(storage, storagePath))
  } catch (error) {
    if (error instanceof FirebaseError && error.code === "storage/object-not-found") return
    throw error
  }
}

export { buildReportId, listMonthlyReports, getMonthlyReport, getMonthlyReportDetail, saveMonthlyReport }
